import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const CALENDLY_URL = 'https://calendly.com/mandieozee/15min';

const credentials = [
  'Clinical Psychologist',
  'Licensed Therapist',
  'Certified Coach',
  'Retreat Facilitator',
  'Conference Host',
  'Speaker',
];

export default function AboutTeaserSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        contentRef.current,
        { opacity: 0, x: -40 },
        {
          opacity: 1,
          x: 0,
          duration: 1,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
          },
        }
      );
      gsap.fromTo(
        imageRef.current,
        { opacity: 0, x: 40 },
        {
          opacity: 1,
          x: 0,
          duration: 1,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="relative overflow-hidden">
      <div className="flex flex-col lg:flex-row min-h-[auto] lg:min-h-[700px]">
        {/* Left — Rich Black */}
        <div className="w-full lg:w-1/2 bg-[#1A1A1A] flex flex-col justify-center px-6 md:px-10 lg:px-16 py-12 lg:py-0">
          <div ref={contentRef}>
            <p className="text-[#C9A84C] text-xs font-body tracking-[0.25em] uppercase mb-6">
              The woman behind the work
            </p>

            <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl leading-[1.2] text-[#FDFAF8] mb-6">
              I did not choose this work.
              <br />
              This work chose me.
            </h2>

            <p className="font-body text-sm md:text-base text-[#E8E8E8] leading-relaxed mb-8 max-w-lg">
              For a long time, I carried a quiet hunger, to be seen, to be loved, to
              be accepted. And beneath all of it, a need to please that kept me
              endlessly giving to others what I was terrified to ask for myself. I
              understood the psychology. I could hold space for everyone around me. And
              still, the thing I most wanted, to simply be enough, as I was, felt
              just out of reach.
            </p>
            <p className="font-body text-sm md:text-base text-[#E8E8E8] leading-relaxed mb-8 max-w-lg">
              That journey, and what it took to finally walk toward it, is the
              foundation of everything I do.
            </p>
            <p className="font-body text-sm md:text-base text-[#E8E8E8] leading-relaxed mb-8 max-w-lg">
              Today I bring deep clinical expertise, a master-level coaching
              certification, and training in somatic and trauma-informed healing into
              every room I enter. But what my clients remember is not my credentials.
              It is how they felt, finally seen, finally loved, finally free to stop
              performing for everyone else&apos;s comfort.
            </p>
            <p className="font-body text-sm md:text-base text-[#E8E8E8] leading-relaxed mb-8 max-w-lg">
              I have walked alongside women through 1:1 and group settings, holding
              each one as if she is already whole.
            </p>
            <p className="font-body text-sm md:text-base text-[#E8E8E8] leading-relaxed mb-8 max-w-lg">
              Because she is. And because I once had to find my way back to that truth
              myself.
            </p>

            {/* Credential Badges */}
            <div className="flex flex-wrap gap-2 mb-8">
              {credentials.map((cred, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-4 py-2 border border-[#C9A84C] text-[#C9A84C] text-xs font-body rounded-full"
                >
                  {cred}
                </span>
              ))}
            </div>

            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-7 py-3.5 border border-[#C9A84C] text-[#C9A84C] text-sm font-body font-medium rounded-full hover:bg-[#C9A84C] hover:text-[#1A1A1A] transition-colors duration-300 whitespace-nowrap"
            >
              Work with Mandie
            </a>
          </div>
        </div>

        {/* Right — Full-bleed Image */}
        <div className="w-full lg:w-1/2 relative min-h-[320px] lg:min-h-auto overflow-hidden">
          <div ref={imageRef} className="absolute inset-0">
            <img
              src="/about-teaser.jpg"
              alt="Mandie Ozee in a warm healing space"
              className="w-full h-full object-cover object-center"
              width="800"
              height="1000"
            />
          </div>
          {/* Subtle gradient overlay so the image blends into the edge */}
          <div className="absolute inset-0 bg-gradient-to-l from-transparent to-transparent pointer-events-none" />
        </div>
      </div>
    </section>
  );
}